import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useEditor } from '../state/editorStore';
import { api } from '../api/client';
import { loadTranscriptIntoEditor, saveEdits, NeedsAuthError } from '../lib/pipeline';
import { loadDemo } from '../demo/loadDemo';
import { Logo } from '../components/Logo';
import { StudioLeftPanel } from '../components/studio/StudioLeftPanel';
import { StudioStage } from '../components/studio/StudioStage';
import { StudioInspector } from '../components/studio/StudioInspector';
import { StudioHookInspector } from '../components/studio/StudioHookInspector';
import { StudioTimeline } from '../components/studio/StudioTimeline';
import { StudioExportButton } from '../components/studio/StudioExportButton';
import { StudioAspectSelect } from '../components/studio/StudioAspectSelect';
import { StudioDepthDialog } from '../components/studio/StudioDepthDialog';
import { StudioMobileKmagic } from '../components/studio/StudioMobileKmagic';
import { playbackClock } from '../playback/clock';

/** True on phone-sized screens (below tablet). Drives the kMagic-only layout. */
function useIsMobile(): boolean {
  const query = '(max-width: 768px)';
  const [m, setM] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setM(mq.matches);
    mq.addEventListener('change', on);
    setM(mq.matches);
    return () => mq.removeEventListener('change', on);
  }, []);
  return m;
}

function fmtTime(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** Minimal play/seek transport for the mobile kMagic view (the full word
 *  timeline is desktop-only). Driven by the shared playbackClock. */
function StudioMobileTransport() {
  const durationMs = useEditor((s) => s.durationMs);
  const [t, setT] = useState(0);
  const [playing, setPlaying] = useState(false);
  useEffect(() => playbackClock.subscribe((ms, isPlaying) => { setT(ms); setPlaying(isPlaying); }), []);
  const dur = Math.max(1, durationMs);
  return (
    <div className="stm-transport">
      <button className="stm-play" onClick={() => playbackClock.toggle()} aria-label={playing ? 'Pause' : 'Play'}>
        {playing ? (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden><rect x="6" y="5" width="4" height="14" rx="1" /><rect x="14" y="5" width="4" height="14" rx="1" /></svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden><path d="M7 5.5v13a1 1 0 0 0 1.5.87l11-6.5a1 1 0 0 0 0-1.74l-11-6.5A1 1 0 0 0 7 5.5Z" /></svg>
        )}
      </button>
      <input
        className="stm-seek"
        type="range"
        min={0}
        max={dur}
        step={10}
        value={Math.min(t, dur)}
        onChange={(e) => playbackClock.seek(Number(e.target.value))}
        aria-label="Seek"
      />
      <span className="stm-time">{fmtTime(t)} / {fmtTime(durationMs)}</span>
    </div>
  );
}

/**
 * Top-bar watermark toggle. Paid plans can turn the export watermark on/off;
 * free/trial plans see it locked ON (the server forces it) and clicking prompts
 * an upgrade. Plan comes from /billing/me; when unknown we assume free (locked).
 */
function WatermarkToggle() {
  const nav = useNavigate();
  const watermark = useEditor((s) => s.watermark);
  const setWatermark = useEditor((s) => s.setWatermark);
  const { data } = useQuery({
    queryKey: ['billing-me'],
    queryFn: () => api.getBilling(),
    retry: false,
    staleTime: 60_000,
  });
  const forced = data?.entitlements.limits.watermark ?? true; // unknown → free
  const paid = !forced;
  const on = forced ? true : watermark;
  return (
    <button
      type="button"
      className={`st-wm${on ? ' on' : ''}${paid ? '' : ' locked'}`}
      aria-pressed={on}
      title={
        paid
          ? 'Toggle the Kaption watermark on exports'
          : 'The watermark is included on the free plan — upgrade to remove it'
      }
      onClick={() => (paid ? setWatermark(!watermark) : nav('/pricing'))}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
        <path d="M12 3s5.5 6.2 5.5 10a5.5 5.5 0 0 1-11 0C6.5 9.2 12 3 12 3Z" />
      </svg>
      <span>Watermark {on ? 'On' : 'Off'}</span>
      {!paid && (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <path d="M12 2a5 5 0 0 0-5 5v3H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2h-1V7a5 5 0 0 0-5-5Zm3 8H9V7a3 3 0 0 1 6 0v3Z" />
        </svg>
      )}
    </button>
  );
}

/**
 * /studio - a new minimalist, keyboard-fast editor. Four panels around an
 * always-centered video: left icon rail + transcript, right inspector, bottom
 * word-level millisecond timeline. Independent of the classic /editor.
 */
export function StudioPage() {
  const { projectId } = useParams();
  const saveState = useEditor((s) => s.saveState);
  const markSaved = useEditor((s) => s.markSaved);
  const laneOpen = useEditor((s) => s.leftTab === 'broll' || s.leftTab === 'audio');
  const [name, setName] = useState<string | null>(null);
  const [editingName, setEditingName] = useState(false);

  // Persist a renamed project (trimmed, clamped to the schema's 120 chars).
  // Optimistic: keep the typed name locally, roll back to the server name on error.
  const commitName = async (raw: string) => {
    setEditingName(false);
    const next = raw.trim().slice(0, 120);
    const id = useEditor.getState().projectId;
    if (!id || !next || next === name) return;
    const prev = name;
    setName(next);
    try {
      await api.renameProject(id, next);
    } catch {
      setName(prev); // rename failed - restore the previous name
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!projectId) {
        loadDemo(null);
        setName('Demo');
        return;
      }
      try {
        const t = await api.getTranscript(projectId);
        if (cancelled) return;
        useEditor.setState({ projectId, language: t.language });
        // Load the project's REAL transcript - even when it has zero words (a
        // silent/no-audio clip). Never substitute the built-in demo transcript
        // for a real project; that only belongs to demo mode (no projectId).
        loadTranscriptIntoEditor(t, t.proxyUrl ?? t.sourceUrl);
        try {
          const p = await api.getProject(projectId);
          if (!cancelled) setName(p.name);
        } catch {
          /* name is best-effort */
        }
      } catch {
        if (!cancelled) loadDemo(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  // Debounced autosave - persist words, segments, template + style overrides
  // (incl. the hook emphasis style) 800ms after the last change, same as the
  // classic editor. In demo mode (no project) just clear the dirty flag.
  useEffect(() => {
    if (saveState !== 'dirty') return;
    const id = setTimeout(() => {
      if (!useEditor.getState().projectId) {
        markSaved();
        return;
      }
      saveEdits().catch((err) => {
        useEditor.setState({ saveState: 'dirty' });
        if (!(err instanceof NeedsAuthError)) {
          /* transient - the next edit retries */
        }
      });
    }, 800);
    return () => clearTimeout(id);
  }, [saveState, markSaved]);

  // Keyboard shortcuts for speed.
  const isMobile = useIsMobile();

  const topBar = (
    <header className="studio-top">
        <div className="st-left">
          <Link to="/" className="st-brand">
            <Logo size={16} />
          </Link>
          {editingName ? (
            <input
              className="st-name st-name-edit"
              defaultValue={name ?? ''}
              autoFocus
              maxLength={120}
              placeholder="Untitled"
              onFocus={(e) => e.currentTarget.select()}
              onBlur={(e) => commitName(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.currentTarget.blur();
                else if (e.key === 'Escape') {
                  e.currentTarget.value = name ?? '';
                  setEditingName(false);
                }
              }}
            />
          ) : (
            <button
              type="button"
              className="st-name"
              title={useEditor.getState().projectId ? 'Rename project' : undefined}
              disabled={!useEditor.getState().projectId}
              onClick={() => setEditingName(true)}
            >
              {name ?? 'Untitled'}
            </button>
          )}
        </div>
        <div className="st-right">
          <span className="st-badge">
            <span className="dot" /> Low-res preview
          </span>
          <Link to="/" className="st-btn ghost">
            ← Home
          </Link>
          <StudioAspectSelect />
          <WatermarkToggle />
          <StudioExportButton />
        </div>
      </header>
  );

  // Mobile (below tablet): a dedicated kMagic-only layout - top bar, the video
  // preview with a simple transport, and the kMagic panel full-screen. All the
  // desktop studio chrome (rail, transcript, inspector, timeline) is omitted.
  if (isMobile) {
    return (
      <div className="studio studio-mobile">
        {topBar}
        <StudioStage />
        <StudioMobileKmagic />
        <StudioMobileTransport />
        <StudioDepthDialog />
      </div>
    );
  }

  return (
    <div className={laneOpen ? 'studio lane-open' : 'studio'}>
      {topBar}

      <div className="studio-mid">
        <StudioStage />
        <StudioLeftPanel />
        <StudioInspector />
        <StudioHookInspector />
      </div>

      <StudioTimeline />
      <StudioDepthDialog />
    </div>
  );
}
